class TokenBlacklistService {
  constructor() {
    this.blacklistedTokens = new Set(this.getBlacklistedTokens());
  }

  addToBlacklist(token) {
    if (token) {
      this.blacklistedTokens.add(token);
      this.saveBlacklistedTokens();
    }
  }

  isBlacklisted(token) {
    return this.blacklistedTokens.has(token);
  }

  getBlacklistedTokens() {
    try {
      const stored = localStorage.getItem('blacklistedTokens');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  saveBlacklistedTokens() {
    try {
      localStorage.setItem('blacklistedTokens', JSON.stringify([...this.blacklistedTokens]));
    } catch (error) {
      console.error('Failed to save blacklisted tokens:', error);
    }
  }

  clearExpiredTokens() {
    // Clear tokens older than 24 hours to prevent memory bloat
    const expiredTime = Date.now() - (24 * 60 * 60 * 1000);
    const currentTokens = [...this.blacklistedTokens];
    
    currentTokens.forEach(token => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 < expiredTime) {
          this.blacklistedTokens.delete(token);
        }
      } catch {
        // Invalid token format, remove it
        this.blacklistedTokens.delete(token);
      }
    });
    
    this.saveBlacklistedTokens();
  }
}

export const tokenBlacklistService = new TokenBlacklistService();

// Secure logout function
export async function secureLogout(instance) {
  const token = sessionStorage.getItem('Token');
  
  // Call logout API if available
  try {
    const { logout } = await import('../services/user-service');
    await logout();
  } catch (error) {
    console.error('Logout API call failed:', error);
    // Continue with local cleanup even if API fails
  }
  
  // Add current token to blacklist
  if (token) {
    tokenBlacklistService.addToBlacklist(token);
  }
  
  // Clear all caches and storage using cacheUtils
  const { cacheUtils } = await import('./cacheUtils');
  cacheUtils.clearAuthCache();
  
  // Perform MSAL logout
  if (instance) {
    instance.logoutRedirect();
  }
}

// Token validation middleware
export function validateTokenSecurity(token) {
  if (!token) {
    return { isValid: false, error: 'No token provided' };
  }
  
  if (tokenBlacklistService.isBlacklisted(token)) {
    return { isValid: false, error: 'Token has been invalidated' };
  }
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    if (payload.exp && payload.exp < currentTime) {
      return { isValid: false, error: 'Token has expired' };
    }
    
    return { isValid: true };
  } catch {
    return { isValid: false, error: 'Invalid token format' };
  }
}

// Initialize cleanup on service creation
tokenBlacklistService.clearExpiredTokens();